import { debounce } from 'lodash-es';
import { ReactNode, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { PosEnumSelect } from 'src/core/POS/PosSelect';
import { useUserHasAdvancedSearchV2Feature } from 'src/hooks/useUserHasFeature';
import { useUserHasConsignorMasterUI } from 'src/hooks/useUserIsConsignorMasterUI';
import { useServerUserSetting } from 'src/hooks/useUserSetting';
import {
  createInventorySearchPageUrl,
  createPurchaseSearchPageUrl,
  createSaleSearchPageUrl,
} from 'src/utils/advancedSearchUtils';
import { ContentId } from 'src/utils/constants/contentId';
import { QueryWithViewMode } from 'src/utils/eventQueryUtils';
import {
  ActionOutboxEntityType,
  Feature,
  InventoryViewMode,
  PurchaseViewMode,
  SalesViewMode,
  UserSetting,
} from 'src/WebApiController';

export const ViewModeSelector = <TQuery extends QueryWithViewMode>({
  query,
  entityType,
  initialQuery,
  setFilterQuery,
  setInitialQuery,
  viewModeSettingsId,
  viewModeCidMap,
  viewModeIconMap,
}: {
  entityType:
    | ActionOutboxEntityType.Listing
    | ActionOutboxEntityType.Purchase
    | ActionOutboxEntityType.Sale
    | ActionOutboxEntityType.SellerEvent;
  query: TQuery;
  initialQuery: TQuery;
  setFilterQuery: (newQuery: TQuery) => void;
  setInitialQuery: (newQuery: TQuery) => void;
  viewModeSettingsId:
    | UserSetting.InventoryPageViewMode
    | UserSetting.SalePageViewMode
    | UserSetting.PurchasePageViewMode;
  viewModeCidMap: Record<string, string>;
  viewModeIconMap: Record<string, ReactNode>;
}) => {
  const {
    setUserSetting: setViewModeUserSetting,
    value: defaultViewModeUserSetting,
  } = useServerUserSetting<string>({
    id: viewModeSettingsId,
    currentLoginUserOnly: true,
  });

  const isConsignorMasterView = useUserHasConsignorMasterUI();

  const viewModeCidMapModified = useMemo(() => {
    if (isConsignorMasterView) {
      const newObj = {
        ...viewModeCidMap,
      };
      delete newObj[InventoryViewMode.FlattenedView]; // it's the same for sales, pos
      return newObj;
    }
    return viewModeCidMap;
  }, [isConsignorMasterView, viewModeCidMap]);

  const navigate = useNavigate();

  const redirectToAdvanceSearch = useCallback(
    (searchUrl: string) => {
      setTimeout(() => {
        navigate(searchUrl, {
          state: {
            returnUrl: `${location.pathname}${location.search}`,
          },
        });
      }, 10);
    },
    [navigate]
  );

  const hasInventorySearchV2Feature = useUserHasAdvancedSearchV2Feature(
    ActionOutboxEntityType.Listing
  );
  const hasSalesSearchV2Feature = useUserHasAdvancedSearchV2Feature(
    ActionOutboxEntityType.Sale
  );
  const hasPurchasesSearchV2Feature = useUserHasAdvancedSearchV2Feature(
    ActionOutboxEntityType.Purchase
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const debouncedOnViewModeChange = useCallback(
    debounce((selectedViewMode?: string | null) => {
      if (selectedViewMode) {
        if (
          selectedViewMode === InventoryViewMode.FlattenedView ||
          selectedViewMode === SalesViewMode.FlattenedView ||
          selectedViewMode === PurchaseViewMode.FlattenedView
        ) {
          if (entityType === ActionOutboxEntityType.Listing) {
            // We just want to redirect to the Advance search
            const searchUrl = createInventorySearchPageUrl(
              hasInventorySearchV2Feature
            );
            redirectToAdvanceSearch(searchUrl);
            return;
          } else if (entityType === ActionOutboxEntityType.Sale) {
            // We just want to redirect to the Advance search
            const searchUrl = createSaleSearchPageUrl(hasSalesSearchV2Feature);
            redirectToAdvanceSearch(searchUrl);
            return;
          } else if (entityType === ActionOutboxEntityType.Purchase) {
            // We just want to redirect to the Advance search
            const searchUrl = createPurchaseSearchPageUrl(
              hasPurchasesSearchV2Feature
            );
            redirectToAdvanceSearch(searchUrl);
            return;
          }
        }

        if (selectedViewMode !== query.viewMode) {
          setFilterQuery({
            ...query,
            viewMode: selectedViewMode,
          });
        }
        // We need to update initialQuery, otherwise the quick filter "All"
        // may change the current view mode
        if (selectedViewMode !== initialQuery.viewMode) {
          setInitialQuery({ ...initialQuery, viewMode: selectedViewMode });
        }

        if (selectedViewMode !== defaultViewModeUserSetting) {
          setViewModeUserSetting(selectedViewMode);
        }
      }
    }, 200),
    [query]
  );

  const effectiveViewMode = useMemo(() => {
    const viewMode =
      query.viewMode ||
      defaultViewModeUserSetting ||
      Object.keys(viewModeCidMapModified)[0];

    return viewMode;
  }, [query.viewMode, defaultViewModeUserSetting, viewModeCidMapModified]);

  if (Object.keys(viewModeCidMapModified).length < 2) {
    return null;
  }

  return (
    <PosEnumSelect
      variant="textPlain"
      shape="none"
      value={effectiveViewMode}
      prefixDisplay={viewModeIconMap[effectiveViewMode ?? '']}
      displayText={ContentId.View}
      onChange={debouncedOnViewModeChange}
      valueOptionsContent={viewModeCidMapModified}
      valueOptionsIcon={viewModeIconMap}
    />
  );
};
